import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';

const DailyBonus = ({ className = "" }) => {
  const { currentUser } = useAuth();
  const { claimDailyBonus, canClaimDailyBonus } = useTCoins();
  const [canClaim, setCanClaim] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimedToday, setClaimedToday] = useState(false);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (currentUser) {
      checkDailyBonus();
    }
  }, [currentUser]);

  const checkDailyBonus = async () => {
    try {
      const canClaimToday = await canClaimDailyBonus(currentUser.uid);
      setCanClaim(canClaimToday);
      setClaimedToday(!canClaimToday);
    } catch (error) {
      console.error('Error checking daily bonus:', error);
    }
  };

  const handleClaimBonus = async () => {
    if (!canClaim || isClaiming) return;

    try {
      setIsClaiming(true);
      const success = await claimDailyBonus(currentUser.uid);
      
      if (success) {
        setCanClaim(false);
        setClaimedToday(true);
        setStreak(prev => prev + 1);
        
        // Show success animation
        showSuccessAnimation();
      }
    } catch (error) {
      console.error('Error claiming daily bonus:', error);
    } finally {
      setIsClaiming(false);
    }
  };

  const showSuccessAnimation = () => {
    // Create floating coins animation
    const container = document.createElement('div');
    container.className = 'fixed inset-0 pointer-events-none z-50';
    document.body.appendChild(container);

    for (let i = 0; i < 5; i++) {
      const coin = document.createElement('div');
      coin.innerHTML = '🪙';
      coin.className = 'absolute text-2xl animate-bounce';
      coin.style.left = `${Math.random() * 100}%`;
      coin.style.top = `${Math.random() * 100}%`;
      coin.style.animationDelay = `${i * 0.1}s`;
      coin.style.animationDuration = '2s';
      
      container.appendChild(coin);
      
      setTimeout(() => {
        coin.remove();
      }, 2000);
    }

    setTimeout(() => {
      container.remove();
    }, 3000);
  };

  const getBonusAmount = () => {
    // Base bonus: 10 T Coins
    // Streak bonus: +1 T Coin per day (max 5)
    const baseBonus = 10;
    const streakBonus = Math.min(streak, 5);
    return baseBonus + streakBonus;
  };

  const getNextBonusTime = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const diff = tomorrow - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours} ชั่วโมง ${minutes} นาที`;
  };

  return (
    <div className={`bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-4 text-white ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">🎁</span>
          <h3 className="text-lg font-semibold">Daily Bonus</h3>
        </div>
        {streak > 0 && (
          <div className="text-sm bg-white bg-opacity-20 px-2 py-1 rounded-full">
            🔥 {streak} วัน
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="text-sm opacity-90 mb-1">โบนัสวันนี้:</div>
        <div className="text-2xl font-bold">
          🪙 {getBonusAmount()} T Coins
        </div>
        {streak > 0 && (
          <div className="text-sm opacity-90">
            +{Math.min(streak, 5)} T Coins (Streak Bonus)
          </div>
        )}
      </div>

      {canClaim ? (
        <button
          onClick={handleClaimBonus}
          disabled={isClaiming}
          className="w-full bg-white text-orange-500 font-semibold py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isClaiming ? 'กำลังรับโบนัส...' : 'รับโบนัสวันนี้'}
        </button>
      ) : (
        <div className="text-center">
          <div className="text-sm opacity-90 mb-2">
            ✅ รับโบนัสแล้ววันนี้
          </div>
          <div className="text-xs opacity-75">
            โบนัสถัดไปใน: {getNextBonusTime()}
          </div>
        </div>
      )}

      <div className="mt-3 text-xs opacity-75">
        💡 เข้าใช้ทุกวันเพื่อรับโบนัสและเพิ่ม Streak!
      </div>
    </div>
  );
};

export default DailyBonus;






